import React, {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Box, Divider, Stack} from "@mui/material";
import moment from "moment";
import {grey} from "@mui/material/colors";
import {isMohMessage, MessageBox, UserTalkingBox} from "./message-box";
import {useAtom, useSetAtom} from "jotai";
import {SelectedConversationAudioPlayer, SelectedMessageAudioPlayer} from "../atoms";
import {createAudioLoader, ensureAudioSource, isAbortError} from "../../../../utils/audioLoader";
import {AudioBar} from "../../../../components/AudioBar";
import {usePlaybackRate} from "../../../../hooks/usePlaybackRate";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {ConversationInteractions} from "./conversation-interactions";

export const SupervisionHistoryItem = ({
                                         conversation,
                                         messages,
                                         project,
                                         updateConversation,
                                         loadDuration = true,
                                         showDebug = true,
                                         showUserTalking = false,
                                         playerSlot = null,
                                         fetchMessages = () => {}
                                       }) => {
  const [selectedAudioConversation, setSelectedAudioConversation] = useAtom(SelectedConversationAudioPlayer);
  const setSelectedMessage = useSetAtom(SelectedMessageAudioPlayer);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // the conversation's recording was downloaded (its player is shown)
  const [audioUrl, setAudioUrl] = useState(null);
  const audioLoaderRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [agentTalking, setAgentTalkingId] = useState(null);
  const [playbackRate, cycleRate] = usePlaybackRate(audioRef, [audioUrl]);

  useEffect(() => {
    if (!selectedAudioConversation) {
      const audio = audioRef.current;
      if (!audio) return;

      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      }
    }
  }, [selectedAudioConversation, isPlaying])

  useEffect(() => {
    setAgentTalkingId(null);
  }, [messages?.length]);

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
      if (selectedAudioConversation) {
        setSelectedAudioConversation(prev => ({...prev, currentTime: value}))
      }
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setSelectedAudioConversation({...conversation, currentTime: 0, duration: duration}); // only set when play
      setSelectedMessage(null);
      ensureAudioSource(audio, audioLoaderRef.current)
        .then(ready => ready && audio.play())
        .then(played => played !== false && setIsPlaying(true))
        .catch(error => {
          if (!isAbortError(error)) console.error("Failed to play the recording:", error);
        });
    }
  };

  useEffect(() => {
    const shouldEndOrTransfer = messages?.some(msg =>
        msg?.end_message === true ||
        (msg?.transfer_number !== null && msg?.transfer_number !== "")
    );
    if (shouldEndOrTransfer && conversation?.status === "ACTIVE") {
      console.log("Conversation has ended or was transferred.");
      ConversationRepository.getConversation({id: conversation?.id})
          .then(response => {
            updateConversation(response?.data);
          })
    }
  }, [messages, conversation?.id, conversation?.status]);

  const isActive = conversation?.status === "ACTIVE";
  useEffect(() => {
    setAudioUrl(null);
    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);
    // An active call has no recording yet; the download starts once it has ended
    if (conversation?.id && !isActive) {
      // recording by conversation id, downloaded (as a blob, it needs the Authorization header) as soon as the
      // conversation is opened; the player is shown only when there is one (the DTO does not say)
      const audio = new Audio();
      const loader = createAudioLoader(({signal}) => ConversationRepository.getConversationAudio({
        id: conversation.id, flowUid: conversation.flowUid || project?.id, signal,
      }));
      audio.preload = "metadata";
      audioRef.current = audio;
      audioLoaderRef.current = loader;
      ensureAudioSource(audio, loader)
        .then(ready => ready && setAudioUrl(`conversation-${conversation.id}`))
        .catch(error => {
          // 404: no recording (e.g. a chat conversation)
          if (!isAbortError(error) && error?.response?.status !== 404) {
            console.error("Failed to load the recording:", error);
          }
        });

      const handleMetadata = () => {
        setDuration(audio.duration);
        setCurrentTime(0);
      };

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        setSelectedAudioConversation(prev => ({...prev, currentTime: audio.currentTime}));
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audio.addEventListener("loadedmetadata", handleMetadata);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("ended", handleEnded);

      return () => {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
        audio.removeEventListener("loadedmetadata", handleMetadata);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("ended", handleEnded);
        loader.dispose();
        if (audioRef.current === audio) audioRef.current = null;
      };
    }
  }, [conversation?.id, isActive]);

  useEffect(() => {
    if (
      selectedAudioConversation?.id !== conversation?.id &&
      audioRef.current
    ) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [selectedAudioConversation]);

  const setAgentTalking = (message, isTalking) => {
    setAgentTalkingId(prev => {
      if (isTalking) {
        // Only set if message.id is newer than current
        return (prev === null || message.id > prev) ? message.id : prev;
      } else {
        // Only clear if this message is >= current agentTalking
        return (prev !== null && message.id >= prev) ? null : prev;
      }
    });
  };


  const isLastMessage = useMemo(() => {
    if (conversation.status !== "ACTIVE") return false;

    const withoutDebugMessage = messages
      ?.filter(i => i?.role !== "DEBUG")
      ?.sort((a, b) => (a.id > b.id ? 1 : -1)); // sort by ID ascending

    const lastMessage = withoutDebugMessage?.[withoutDebugMessage.length - 1];

    return lastMessage?.last_message && lastMessage?.role === "AGENT";
  }, [conversation, messages?.length]);


  // The hold music plays from a music-on-hold message until the next AGENT or USER message (only while the call is
  // active): the indicator is shown only while it is the latest message
  const playingMohId = useMemo(() => {
    if (conversation?.status !== "ACTIVE") return null;
    const lastMessage = [...(messages || [])]
      .filter(item => ["AGENT", "USER"].includes(item?.role || item?.sender))
      .sort((a, b) => (a.id > b.id ? 1 : -1))
      .pop();
    return isMohMessage(lastMessage) ? lastMessage.id : null;
  }, [conversation?.status, messages]);

  return (
    <Stack direction="column" gap={1} sx={{p: 2}}>
      <Divider
        sx={{
          width: "90%",
          mx: "auto",
          textTransform: "capitalize",
          fontSize: "0.85em",
          color: grey[600],
          fontWeight: 500
        }}
      >
        {moment(conversation?.dateCreated).format("DD MMM YYYY HH:mm")}
      </Divider>

      {messages.map((msg, index) => {
        const filtered = showDebug ? [...messages] : [...messages]?.filter(i => i?.role !== "DEBUG");
        const ind = filtered?.findIndex(i => i?.id === msg?.id);
        return (
          <MessageBox
            key={msg.id}
            message={msg}
            conversationId={conversation?.id}
            prevMessage={filtered[ind - 1] || null}
            project={project}
            conversationType={conversation?.content?.type || conversation?.type || project?.settings?.projectType}
            loadDuration={loadDuration}
            showDebug={showDebug}
            mohPlaying={msg.id === playingMohId}
            setAgentTalking={setAgentTalking}
            fetchMessages={fetchMessages}
          />
        )
      })}
      {showUserTalking && isLastMessage && !agentTalking && !playingMohId && <UserTalkingBox/>}

      {audioUrl && (() => {
        const bar = (
          <AudioBar
            playing={isPlaying}
            currentTime={currentTime}
            duration={duration}
            rate={playbackRate}
            onToggle={togglePlay}
            onSeek={handleSeek}
            onCycleRate={cycleRate}
          />
        );
        return playerSlot ? createPortal(bar, playerSlot) : <Box sx={{mt: 4}}>{bar}</Box>;
      })()}

      <ConversationInteractions conversation={conversation} updateConversation={updateConversation} />

    </Stack>
  );
};
